/**
 * PersonalInfoTab - Employee personal information tab
 * Extracted from EmployeeDetailPage to reduce file size
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Edit,
  Save,
  User,
  MapPin,
  FileText,
  Briefcase,
  CreditCard,
  Shield,
} from "lucide-react";

const roleOptions = [
  { value: "pcw", label: "Personal Care Worker (PCW)" },
  { value: "hha", label: "Home Health Aide (HHA)" },
  { value: "rn", label: "Registered Nurse (RN)" },
  { value: "lpn", label: "Licensed Practical Nurse (LPN)" },
  { value: "pt", label: "Physical Therapist" },
  { value: "ot", label: "Occupational Therapist" },
  { value: "sw", label: "Social Worker" },
  { value: "case_manager", label: "Case Manager" },
  { value: "hr_scheduler", label: "HR / Scheduler" },
  { value: "evv_specialist", label: "EVV Specialist" },
  { value: "intake_coordinator", label: "Intake Coordinator" },
  { value: "receptionist", label: "Receptionist" },
  { value: "admin", label: "Administrator" },
];

export function PersonalInfoTab({
  employee,
  editingPersonalData,
  setEditingPersonalData,
  personalData,
  setPersonalData,
  savingPersonalData,
  handleSavePersonalData,
  handleCancelEdit,
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-slate-800">Personal Information</h3>
        {editingPersonalData ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSavePersonalData}
              disabled={savingPersonalData}
              className="gap-2"
            >
              <Save className="w-4 h-4" />
              {savingPersonalData ? "Saving..." : "Save"}
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            onClick={() => setEditingPersonalData(true)}
            className="gap-2"
            data-testid="edit-personal-btn"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Button>
        )}
      </div>

      {/* Basic Info Section */}
      <div className="mb-8">
        <h4 className="text-sm font-semibold text-slate-600 mb-4 flex items-center gap-2">
          <User className="w-4 h-4" />
          Basic Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Full Name *</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.name}
                onChange={(e) => setPersonalData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Employee name"
              />
            ) : (
              <p className="text-slate-800 font-medium">{employee.name || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Email (login)</Label>
            {editingPersonalData ? (
              <div>
                <Input type="email" value={personalData.email} readOnly disabled className="bg-slate-50" data-testid="employee-email-input" />
                <p className="text-[11px] text-slate-400 mt-1">Login email can't be changed here — remove and re-add the employee to use a new email.</p>
              </div>
            ) : (
              <p className="text-slate-800">{employee.email || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Phone</Label>
            {editingPersonalData ? (
              <Input
                type="tel"
                value={personalData.phone}
                onChange={(e) => setPersonalData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="555-123-4567"
              />
            ) : (
              <p className="text-slate-800">{employee.phone || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Role</Label>
            {editingPersonalData ? (
              <Select
                value={(personalData.role || "").toLowerCase()}
                onValueChange={(value) => setPersonalData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-slate-800 capitalize">{employee.role?.replace("_", " ") || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Status</Label>
            {editingPersonalData ? (
              <Select
                value={personalData.status}
                onValueChange={(value) => setPersonalData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                employee.status === "active" ? "bg-emerald-100 text-emerald-700" :
                employee.status === "on_leave" ? "bg-amber-100 text-amber-700" :
                employee.status === "on_hold" ? "bg-violet-100 text-violet-700" :
                "bg-slate-100 text-slate-600"
              }`}>
                {employee.status === "on_hold" ? "on hold" : (employee.status || "active")}
              </span>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Employee ID</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.employee_id}
                onChange={(e) => setPersonalData(prev => ({ ...prev, employee_id: e.target.value }))}
                placeholder="EMP-001"
              />
            ) : (
              <p className="text-slate-800 font-mono">{employee.employee_id || "-"}</p>
            )}
          </div>
        </div>
      </div>

      {/* Address Section */}
      <div className="mb-8">
        <h4 className="text-sm font-semibold text-slate-600 mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Address
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs text-slate-500">Street Address</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.address}
                onChange={(e) => setPersonalData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="123 Main St"
              />
            ) : (
              <p className="text-slate-800">{employee.address || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">City</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.city}
                onChange={(e) => setPersonalData(prev => ({ ...prev, city: e.target.value }))}
                placeholder="City"
              />
            ) : (
              <p className="text-slate-800">{employee.city || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">State</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.state}
                onChange={(e) => setPersonalData(prev => ({ ...prev, state: e.target.value }))}
                placeholder="WI"
                maxLength={2}
              />
            ) : (
              <p className="text-slate-800">{employee.state || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">ZIP Code</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.zip}
                onChange={(e) => setPersonalData(prev => ({ ...prev, zip: e.target.value }))}
                placeholder="12345"
              />
            ) : (
              <p className="text-slate-800">{employee.zip || "-"}</p>
            )}
          </div>
        </div>
      </div>

      {/* Employment Details Section */}
      <div className="mb-8">
        <h4 className="text-sm font-semibold text-slate-600 mb-4 flex items-center gap-2">
          <Briefcase className="w-4 h-4" />
          Employment Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Hire Date</Label>
            {editingPersonalData ? (
              <Input
                type="date"
                value={personalData.hire_date}
                onChange={(e) => setPersonalData(prev => ({ ...prev, hire_date: e.target.value }))}
              />
            ) : (
              <p className="text-slate-800">{employee.hire_date || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Pay Rate</Label>
            {editingPersonalData ? (
              <Input
                type="number"
                step="0.01"
                value={personalData.pay_rate}
                onChange={(e) => setPersonalData(prev => ({ ...prev, pay_rate: e.target.value }))}
                placeholder="15.00"
              />
            ) : (
              <p className="text-slate-800">{employee.pay_rate ? `$${employee.pay_rate}/hr` : "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Pay Type</Label>
            {editingPersonalData ? (
              <Select
                value={personalData.pay_type}
                onValueChange={(value) => setPersonalData(prev => ({ ...prev, pay_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="salary">Salary</SelectItem>
                  <SelectItem value="per_visit">Per Visit</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-slate-800 capitalize">{employee.pay_type || "hourly"}</p>
            )}
          </div>
          {/* RN-only per-visit rate fields */}
          {employee.role === "rn" && (
            <>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Assessment Visit Rate</Label>
                {editingPersonalData ? (
                  <Input
                    type="number"
                    step="0.01"
                    value={personalData.assessment_visit_rate || ""}
                    onChange={(e) => setPersonalData(prev => ({ ...prev, assessment_visit_rate: e.target.value }))}
                    placeholder="e.g., 75.00"
                    data-testid="edit-assessment-visit-rate"
                  />
                ) : (
                  <p className="text-slate-800">
                    {employee.assessment_visit_rate ? `$${employee.assessment_visit_rate}/visit` : "-"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Supervisory Visit Rate</Label>
                {editingPersonalData ? (
                  <Input
                    type="number"
                    step="0.01"
                    value={personalData.supervisory_visit_rate || ""}
                    onChange={(e) => setPersonalData(prev => ({ ...prev, supervisory_visit_rate: e.target.value }))}
                    placeholder="e.g., 50.00"
                    data-testid="edit-supervisory-visit-rate"
                  />
                ) : (
                  <p className="text-slate-800">
                    {employee.supervisory_visit_rate ? `$${employee.supervisory_visit_rate}/visit` : "-"}
                  </p>
                )}
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Documentation Incentive ($/hr)</Label>
            {editingPersonalData ? (
              <Input
                type="number"
                step="0.01"
                min="0"
                value={personalData.documentation_incentive_rate || ""}
                onChange={(e) => setPersonalData(prev => ({ ...prev, documentation_incentive_rate: e.target.value }))}
                placeholder="e.g., 1.50"
                data-testid="edit-documentation-incentive-rate"
              />
            ) : (
              <p className="text-slate-800">
                {employee.documentation_incentive_rate ? `$${employee.documentation_incentive_rate}/hr` : "-"}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Department</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.department}
                onChange={(e) => setPersonalData(prev => ({ ...prev, department: e.target.value }))}
                placeholder="Home Care"
              />
            ) : (
              <p className="text-slate-800">{employee.department || "-"}</p>
            )}
          </div>
        </div>
      </div>

      {/* License & Certifications Section */}
      <div className="mb-8">
        <h4 className="text-sm font-semibold text-slate-600 mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          License & Certifications
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">License Number</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.license_number}
                onChange={(e) => setPersonalData(prev => ({ ...prev, license_number: e.target.value }))}
                placeholder="License #"
              />
            ) : (
              <p className="text-slate-800">{employee.license_number || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">License Expiry</Label>
            {editingPersonalData ? (
              <Input
                type="date"
                value={personalData.license_expiry}
                onChange={(e) => setPersonalData(prev => ({ ...prev, license_expiry: e.target.value }))}
              />
            ) : (
              <p className="text-slate-800">{employee.license_expiry || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">EVV Number</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.evv_number}
                onChange={(e) => setPersonalData(prev => ({ ...prev, evv_number: e.target.value }))}
                placeholder="EVV ID"
                data-testid="evv-number-input"
              />
            ) : (
              <p className="text-slate-800">{employee.evv_number || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Certifications</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.certifications}
                onChange={(e) => setPersonalData(prev => ({ ...prev, certifications: e.target.value }))}
                placeholder="CPR, First Aid, etc."
              />
            ) : (
              <p className="text-slate-800">{employee.certifications || "-"}</p>
            )}
          </div>
        </div>
      </div>

      {/* Sandata/Provider IDs Section */}
      <div className="mb-8">
        <h4 className="text-sm font-semibold text-slate-600 mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4" />
          Provider IDs (Sandata/EVV)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Provider ID</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.provider_id}
                onChange={(e) => setPersonalData(prev => ({ ...prev, provider_id: e.target.value }))}
                placeholder="9-15 digits"
                data-testid="provider-id-input"
              />
            ) : (
              <p className="text-slate-800 font-mono">{employee.provider_id || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">NPI Number</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.npi_number}
                onChange={(e) => setPersonalData(prev => ({ ...prev, npi_number: e.target.value }))}
                placeholder="10 digits"
              />
            ) : (
              <p className="text-slate-800 font-mono">{employee.npi_number || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">SSN (Last 4)</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.ssn_last4}
                onChange={(e) => setPersonalData(prev => ({ ...prev, ssn_last4: e.target.value }))}
                placeholder="1234"
                maxLength={4}
              />
            ) : (
              <p className="text-slate-800">
                {employee.ssn_last4 ? `***-**-${employee.ssn_last4}` : "-"}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Emergency Contact Section */}
      <div className="mb-8">
        <h4 className="text-sm font-semibold text-slate-600 mb-4 flex items-center gap-2">
          <CreditCard className="w-4 h-4" />
          Emergency Contact
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Contact Name</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.emergency_contact_name}
                onChange={(e) => setPersonalData(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                placeholder="Emergency contact name"
              />
            ) : (
              <p className="text-slate-800">{employee.emergency_contact_name || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Contact Phone</Label>
            {editingPersonalData ? (
              <Input
                type="tel"
                value={personalData.emergency_contact_phone}
                onChange={(e) => setPersonalData(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                placeholder="555-123-4567"
              />
            ) : (
              <p className="text-slate-800">{employee.emergency_contact_phone || "-"}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Relationship</Label>
            {editingPersonalData ? (
              <Input
                value={personalData.emergency_contact_relationship}
                onChange={(e) => setPersonalData(prev => ({ ...prev, emergency_contact_relationship: e.target.value }))}
                placeholder="Spouse, Parent, etc."
              />
            ) : (
              <p className="text-slate-800">{employee.emergency_contact_relationship || "-"}</p>
            )}
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <div>
        <h4 className="text-sm font-semibold text-slate-600 mb-4">Notes</h4>
        {editingPersonalData ? (
          <Textarea
            value={personalData.notes}
            onChange={(e) => setPersonalData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Additional notes about this employee..."
            rows={3}
          />
        ) : (
          <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">
            {employee.notes || "No notes added"}
          </p>
        )}
      </div>
    </div>
  );
}
